"use client";

import { c as _c } from "react/compiler-runtime";
import { InterestRate } from "@justhome/api-core";
import { Checkbox, CheckboxGroup } from "@justhome/react-ui";
import { CategoryScale, Chart as ChartJS, Filler, LineElement, LinearScale, PointElement, Title, Tooltip } from "chart.js";
import CrosshairPlugin from "chartjs-plugin-crosshair";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { lines } from "../../lib/constants";
import { InterestRateChartType } from "../../lib/types";
import { getDatasetConfig, getOptions } from "../../lib/utils";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler, CrosshairPlugin);
interface InterestRateChartProps {
  data: InterestRate[];
  chartType: InterestRateChartType;
  period: "6m" | "12m" | "60m" | "120m" | "180m";
  disableAutoHeight?: boolean;
  width?: number;
  height?: number;
}
const getEvenlyDistributedDates = (data: InterestRate[], period: "6m" | "12m" | "60m" | "120m" | "180m") => {
  return data.map(item => {
    const date = new Date(item.createdAt);
    switch (period) {
      case "6m":
        return format(date, "d MMM");
      case "12m":
      case "60m":
        return format(date, "MMM yyyy");
      case "120m":
      case "180m":
        return format(date, "yyyy");
      default:
        return format(date, "MMM yyyy");
    }
  });
};
const InterestRateChart = t0 => {
  const $ = _c(26);
  const {
    data,
    period,
    chartType,
    disableAutoHeight: t1
  } = t0;
  const disableAutoHeight = t1 === undefined ? false : t1;
  const chartRef = useRef(null);
  let t2;
  if ($[0] !== data || $[1] !== period) {
    t2 = getEvenlyDistributedDates(data, period);
    $[0] = data;
    $[1] = period;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const labels = t2;
  const containerRef = useRef(null);
  const chartContainerRef = useRef(null);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["tenYearInterestRate"];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [visibleLines, setVisibleLines] = useState(t3);
  let t4;
  if ($[4] !== chartType || $[5] !== data || $[6] !== visibleLines) {
    t4 = lines.map(line => getDatasetConfig(!visibleLines.includes(line.id), chartType, line, data));
    $[4] = chartType;
    $[5] = data;
    $[6] = visibleLines;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== labels || $[9] !== t4) {
    t5 = {
      labels,
      datasets: t4
    };
    $[8] = labels;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const chartData = t5;
  let t6;
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const handleBeforePrint = () => {
        if (chartRef.current && chartContainerRef.current) {
          chartRef.current.resize(700, 300);
          chartContainerRef.current.style.width = "700px";
          chartContainerRef.current.style.height = "300px";
        }
      };
      const handleAfterPrint = () => {
        if (chartRef.current && chartContainerRef.current) {
          chartRef.current.resize();
          chartContainerRef.current.style.width = "";
          chartContainerRef.current.style.height = "";
        }
      };
      window.addEventListener("beforeprint", handleBeforePrint);
      window.addEventListener("afterprint", handleAfterPrint);
      return () => {
        window.removeEventListener("beforeprint", handleBeforePrint);
        window.removeEventListener("afterprint", handleAfterPrint);
      };
    };
    t7 = [];
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  const t8 = disableAutoHeight ? 80 : undefined;
  let t9;
  if ($[13] !== chartType || $[14] !== period) {
    t9 = getOptions(period, chartType);
    $[13] = chartType;
    $[14] = period;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== chartData || $[17] !== t8 || $[18] !== t9) {
    t10 = <div id="chart-container" className="w-full h-[200px] m:h-[400px]" ref={chartContainerRef}><Line height={t8} ref={chartRef} options={t9} data={chartData} /></div>;
    $[16] = chartData;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = lines.map(_temp);
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== visibleLines) {
    t12 = <div className="print:hidden my-m flex justify-center m:justify-end"><CheckboxGroup name="interestRateLines" value={visibleLines} onChange={setVisibleLines} label="Sollzinsbindung" id="interestRateLines">{t11}</CheckboxGroup></div>;
    $[21] = visibleLines;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t12) {
    t13 = <div className="w-full relative" ref={containerRef} id="chart-wrapper">{t10}{t12}</div>;
    $[23] = t10;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
};
export default InterestRateChart;
function _temp(line_0) {
  return <Checkbox value={line_0.id} key={line_0.id} variant={line_0.color}><span className="whitespace-nowrap">{line_0.label}</span></Checkbox>;
}